import { useI18n } from '@cascade/react/i18n';
import { Checkbox, Input, Select, TextArea } from '@cascade/ui-library';
import { isOverLength } from './TicketField.helpers.ts';
import type { TTicketFieldProps } from './TicketField.types.ts';

const TEXT_ROWS = 3;

export const TicketField = ({
  field,
  value,
  errorMessage,
  disabled,
  onChange,
}: TTicketFieldProps) => {
  const { translate } = useI18n();

  const { name, type, placeholder, required, editable, values } = field;
  const isEditable = editable !== false;
  const isDisabled = disabled || !isEditable;
  const text = typeof value === 'string' ? value : '';
  const trimmedLength = text.trim().length;
  const isTooLong = isOverLength(field, value);

  const feedbackText =
    errorMessage ??
    (field.max_length !== undefined
      ? translate('ticket.create.length', {
          length: trimmedLength,
          max: field.max_length,
        })
      : undefined);

  const feedbackLevel =
    errorMessage !== undefined || isTooLong ? 'danger' : undefined;

  if (type === 'boolean') {
    return (
      <Checkbox
        label={name}
        value={value === true}
        required={required}
        disabled={isDisabled}
        feedbackLevel={feedbackLevel}
        feedbackText={feedbackText}
        onChange={onChange}
      />
    );
  }

  if (type === 'choice' || type === 'reference') {
    const options = values.map((option) => ({
      label: option.name,
      value: option.id,
    }));

    const hasOptions = options.length > 0;
    const emptyText = hasOptions
      ? undefined
      : translate('ticket.create.noOptions');

    return (
      <Select
        label={name}
        options={options}
        value={text || undefined}
        placeholder={placeholder}
        required={required}
        disabled={isDisabled || !hasOptions}
        canSearch
        feedbackLevel={feedbackLevel}
        feedbackText={feedbackText ?? emptyText}
        onChange={onChange}
      />
    );
  }

  if (type === 'text') {
    return (
      <TextArea
        label={name}
        value={text}
        placeholder={placeholder}
        rows={TEXT_ROWS}
        required={required}
        disabled={isDisabled}
        feedbackLevel={feedbackLevel}
        feedbackText={feedbackText}
        onChange={onChange}
      />
    );
  }

  return (
    <Input
      label={name}
      value={text}
      placeholder={placeholder}
      required={required}
      disabled={isDisabled}
      feedbackLevel={feedbackLevel}
      feedbackText={feedbackText}
      onChange={onChange}
    />
  );
};
